"use client";

import { useState, useEffect, useCallback } from "react";
import { Tabs, Badge, Space, Spin, message } from "antd";
import useContestService from "../../../../services/contestService";
import { GiveawayTab } from "./GiveawayTab";
import { WinnerTab } from "./WinnerTab";
import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";
import SubscriptionOverlay from "../../../UI/SubscriptionOverlay";
import { useAuth } from "../../../../context/authContext";

// ===== MAIN COMPONENT =====
export default function GiveawayManager() {
  const { language } = useLanguage();
  const t = giveAwayContent[language].giveawayManager;
  const { subscriptionLevel, user, fetchUser } = useAuth();

  // State Management
  const [giveaways, setGiveaways] = useState([]);
  const [winners, setWinners] = useState([]);
  const [activeTab, setActiveTab] = useState("giveaways");
  const [loading, setLoading] = useState(false);
  const [giveawaysLoading, setGiveawaysLoading] = useState(false);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination & Filters
  const [giveawayFilters, setGiveawayFilters] = useState({
    page: 1,
    limit: 20,
    status: "",
  });
  const [giveawayPagination, setGiveawayPagination] = useState(null);

  // Contest Service Hook
  const {
    getContests,
    getContestWinners,
    drawWinners,
    updateWinnerStatus,
    getContestStats,
  } = useContestService();

  // ===== GIVEAWAY FUNCTIONS =====
  const loadGiveaways = useCallback(
    async (filters = giveawayFilters) => {
      setGiveawaysLoading(true);
      setError(null);

      try {
        const result = await getContests(filters);
        if (result && result.contests) {
          setGiveaways(result.contests);
          setGiveawayPagination(result.pagination);
        } else {
          setGiveaways([]);
          setGiveawayPagination(null);
        }
      } catch (error) {
        console.error(t.console.errorLoadGiveaways, error);
        setError(t.errors.loadGiveaways);
        message.error(t.errors.loadGiveawaysShort);
      } finally {
        setGiveawaysLoading(false);
      }
    },
    [getContests, giveawayFilters, t]
  );

  // ===== WINNER FUNCTIONS =====
  const loadAllWinners = useCallback(async () => {
    setWinnersLoading(true);
    setError(null);

    try {
      // Alle Giveaways laden um deren Winner zu bekommen
      const giveawaysResult = await getContests({
        status: "drawn",
        limit: 100,
      });

      if (giveawaysResult && giveawaysResult.contests) {
        const allWinners = [];

        // Für jedes ausgeloste Giveaway die Winner laden
        for (const giveaway of giveawaysResult.contests) {
          try {
            const giveawayWinners = await getContestWinners(giveaway._id);
            if (giveawayWinners && giveawayWinners.length > 0) {
              // Winner mit Giveaway-Info anreichern
              const enrichedWinners = giveawayWinners.map((winner) => ({
                ...winner,
                giveawayTitle: giveaway.title,
                giveawayId: giveaway._id,
              }));
              allWinners.push(...enrichedWinners);
            }
          } catch (error) {
            console.error(
              `${t.errors.loadWinnersForGiveaway} ${giveaway._id}:`,
              error
            );
          }
        }
        console.log(t.console.winners, allWinners);
        setWinners(allWinners);
      } else {
        setWinners([]);
      }
    } catch (error) {
      console.error(t.console.errorLoadWinners, error);
      setError(t.errors.loadWinners);
      message.error(t.errors.loadWinnersShort);
    } finally {
      setWinnersLoading(false);
    }
  }, [getContests, getContestWinners, t]);

  // ===== UTILITY FUNCTIONS =====
  const updateGiveawayFilters = useCallback(
    (newFilters) => {
      const updatedFilters = { ...giveawayFilters, ...newFilters, page: 1 };
      setGiveawayFilters(updatedFilters);
      loadGiveaways(updatedFilters);
    },
    [giveawayFilters, loadGiveaways]
  );

  const handleGiveawayPageChange = useCallback(
    (page) => {
      const updatedFilters = { ...giveawayFilters, page };
      setGiveawayFilters(updatedFilters);
      loadGiveaways(updatedFilters);
    },
    [giveawayFilters, loadGiveaways]
  );

  const refreshGiveaways = useCallback(() => {
    loadGiveaways(giveawayFilters);
  }, [loadGiveaways, giveawayFilters]);

  const refreshWinners = useCallback(() => {
    loadAllWinners();
  }, [loadAllWinners]);

  const refreshAll = useCallback(() => {
    refreshGiveaways();
    refreshWinners();
  }, [refreshGiveaways, refreshWinners]);

  // ===== GIVEAWAY ACTIONS =====
  const handleDrawWinners = useCallback(
    async (giveawayId) => {
      try {
        setLoading(true);
        const result = await drawWinners(giveawayId);

        if (result) {
          message.success(t.success.winnersDrawn);
          // Beide Listen aktualisieren
          refreshGiveaways();
          refreshWinners();
          // Zur Winner-Tab wechseln
          setActiveTab("winners");
        }
      } catch (error) {
        console.error(t.errors.drawWinners, error);
        message.error(t.errors.drawWinnersShort);
      } finally {
        setLoading(false);
      }
    },
    [drawWinners, refreshGiveaways, refreshWinners, t]
  );

  // ===== WINNER ACTIONS =====
  const handleUpdateWinnerStatus = useCallback(
    async (winnerId, status) => {
      try {
        setLoading(true);
        const result = await updateWinnerStatus(winnerId, status);

        if (result) {
          message.success(t.success.winnerStatusUpdated);
          refreshWinners();
        }
      } catch (error) {
        console.error(t.errors.updateWinnerStatus, error);
        message.error(t.errors.updateWinnerStatusShort);
      } finally {
        setLoading(false);
      }
    },
    [updateWinnerStatus, refreshWinners, t]
  );

  // ===== INITIAL LOAD =====
  useEffect(() => {
    loadGiveaways();
    loadAllWinners();
  }, []);

  // ===== TAB CHANGE HANDLER =====
  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);

      // Daten aktualisieren wenn zu Tab gewechselt wird
      if (tab === "giveaways" && giveaways.length === 0) {
        loadGiveaways();
      } else if (tab === "winners" && winners.length === 0) {
        loadAllWinners();
      }
    },
    [giveaways.length, winners.length, loadGiveaways, loadAllWinners]
  );

  // ===== COMPUTED VALUES =====
  const pendingWinners = winners.filter((w) => w.status === "pending").length;
  const isLoading = giveawaysLoading || winnersLoading || loading;

  // ===== SUBSCRIPTION CHECK =====
  // Wenn subscriptionLevel < 4, zeige SubscriptionOverlay
  if (subscriptionLevel < 2) {
    return (
      <div className="w-full">
        <SubscriptionOverlay />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      {/* Orange accent line */}
      <div className="w-full h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600"></div>

      {/* Custom Tab Header */}
      <div className="bg-white shadow-sm border-b-2 border-orange-500">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <div className="flex bg-gray-100 rounded-full p-1 space-x-2 mt-6 mb-6 shadow-inner">
              <button
                onClick={() => handleTabChange("giveaways")}
                disabled={isLoading}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ease-out flex items-center space-x-2 transform ${
                  activeTab === "giveaways"
                    ? "bg-orange-500 text-white shadow-lg scale-105 ring-2 ring-orange-300 ring-opacity-50"
                    : "text-gray-600 hover:text-orange-500 hover:bg-white hover:shadow-md hover:scale-102"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {giveawaysLoading ? (
                  <Spin size="small" />
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
                    />
                  </svg>
                )}
                <span>
                  {t.tabs.giveaways} ({giveaways.length})
                </span>
              </button>

              <button
                onClick={() => handleTabChange("winners")}
                disabled={isLoading}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ease-out flex items-center space-x-2 relative transform ${
                  activeTab === "winners"
                    ? "bg-orange-500 text-white shadow-lg scale-105 ring-2 ring-orange-300 ring-opacity-50"
                    : "text-gray-600 hover:text-orange-500 hover:bg-white hover:shadow-md hover:scale-102"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {winnersLoading ? (
                  <Spin size="small" />
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                )}
                <span>
                  {t.tabs.winners} ({winners.length})
                </span>
                {pendingWinners > 0 && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-lg animate-pulse border-2 border-white">
                    {pendingWinners}
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Refresh Button */}
          <div className="flex justify-center pb-4">
            <button
              onClick={refreshAll}
              disabled={isLoading}
              className="px-4 py-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              {isLoading ? (
                <Spin size="small" />
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              )}
              <span>{t.refresh}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex justify-between items-center">
            <span>{error}</span>
            <button
              onClick={refreshAll}
              className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            >
              {t.retryButton}
            </button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-4 mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-4 shadow-sm">
            <div className="flex items-start space-x-3">
              <svg
                className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-gray-700 text-sm">
                  {language === "de"
                    ? "Falls Sie noch nicht wissen, wie das Ganze funktioniert, besuchen Sie gerne die Tutorial-Seite."
                    : "If you're not sure how this works yet, feel free to visit the tutorial page."}
                </p>
                <a
                  href="/dashboard/tutorials"
                  className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors group"
                >
                  <span>
                    {language === "de" ? "Zum Tutorial" : "Go to Tutorial"}
                  </span>
                  <svg
                    className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-4 bg-white rounded-lg px-3 py-1 shadow-md border border-orange-200 w-auto">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <svg
                className="w-5 h-5 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <span>Nutzung:</span>
              <span className="text-lg font-semibold text-gray-700">
                {user?.reportUsage?.giveAways || 0}/
                {user?.reportLimits?.giveAways || 0}
              </span>
            </div>
            <div className="w-8 h-1 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  (user?.reportUsage?.giveAways || 0) /
                    (user?.reportLimits?.giveAways || 1) >=
                  0.9
                    ? "bg-red-500"
                    : (user?.reportUsage?.giveAways || 0) /
                        (user?.reportLimits?.giveAways || 1) >=
                      0.7
                    ? "bg-yellow-500"
                    : "bg-green-500"
                }`}
                style={{
                  width: `${Math.min(
                    100,
                    ((user?.reportUsage?.giveAways || 0) /
                      (user?.reportLimits?.giveAways || 1)) *
                      100
                  )}%`,
                }}
              />
            </div>
          </div>
        </div>
        {activeTab === "giveaways" && (
          <GiveawayTab
            giveaways={giveaways}
            setGiveaways={setGiveaways}
            setActiveTab={setActiveTab}
            loading={giveawaysLoading}
            pagination={giveawayPagination}
            filters={giveawayFilters}
            onFilterChange={updateGiveawayFilters}
            onPageChange={handleGiveawayPageChange}
            onRefresh={refreshGiveaways}
            onDrawWinners={handleDrawWinners}
            language={language} // ✅ Language prop durchreichen
            // API Functions durchreichen
            contestService={{
              getContests,
              getContestStats,
              drawWinners,
              getContestWinners,
            }}
          />
        )}
        {activeTab === "winners" && (
          <WinnerTab
            winners={winners}
            setWinners={setWinners}
            loading={winnersLoading}
            onRefresh={refreshWinners}
            onUpdateStatus={handleUpdateWinnerStatus}
            language={language} // ✅ Language prop durchreichen
            // API Functions durchreichen
            contestService={{
              updateWinnerStatus,
              getContestWinners,
            }}
          />
        )}
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-3">
            <Spin size="large" />
            <span className="text-lg">{t.processing}</span>
          </div>
        </div>
      )}

      {/* Bottom orange accent */}
      <div className="fixed bottom-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400"></div>
    </div>
  );
}
